import {
	BrowserClient,
	defaultStackParser,
	getDefaultIntegrations,
	makeFetchTransport,
	Scope,
	type Event as SentryEvent,
} from '@sentry/browser';

import { fg } from '@atlaskit/platform-feature-flags';

import { BFFError, BFFNetworkError } from '../../common/errors';
import { getEnvironment } from '../environment';

import { DEFAULT_SENTRY_CONFIG, SENTRY_DSN, SUPPRESSED_BACKEND_ERROR_PATTERNS } from './constants';
import type { SentryClientConfig, SentryEventTags } from './types';

let sentryClient: BrowserClient | undefined;
let scope: Scope = new Scope();

/**
 * Returns true for known BE / network errors not actionable in the FE.
 * @internal exported for unit tests; do not import outside `services/sentry`.
 */
export function isSuppressedBackendError(event: SentryEvent): boolean {
	const exceptionValue = event.exception?.values?.[0]?.value ?? '';
	const candidates = [exceptionValue, event.message ?? ''].filter(Boolean);

	if (
		candidates.length > 0 &&
		SUPPRESSED_BACKEND_ERROR_PATTERNS.some((re) => candidates.some((c) => re.test(c)))
	) {
		return true;
	}

	// BFFNetworkError 5xx via flattened scope.extras (set by logException)
	const extras = event.extra as { name?: string; status?: string | number } | undefined;
	if (extras?.name === 'BFF_NETWORK_ERROR') {
		const status = Number(extras.status);
		if (!Number.isNaN(status) && status >= 500) {
			return true;
		}
	}

	return false;
}

export function logException(exception: Error | unknown, tags: SentryEventTags): void {
	scope.setTags(tags);

	if (exception instanceof BFFError) {
		const error = exception as BFFError;

		scope.setExtras({
			name: error.name,
			context: error.context,
			status: error.status,
			timestamp: error.timestamp,
			originalError: error.originalError,
			...(error instanceof BFFNetworkError && { url: error.url }),
		});
	}

	sentryClient?.captureException(exception, undefined, scope);

	// Unset tags to prevent an instance of the error boundary from logging with a specific tag,
	// and then another instance from wrongly passing it despite not having the tag.
	for (const [key, _] of Object.entries(tags)) {
		scope.setTag(key, undefined);
	}

	// Unset any extras to prevent them from persisting
	scope.setExtras({});
}

export function setupSentryClient({
	sentryConfig,
}: {
	sentryConfig?: SentryClientConfig;
}): boolean {
	const { dsn = SENTRY_DSN, env } = sentryConfig || {};

	if (!sentryClient) {
		// SSR-safe check. If we are in SSR, we don't need a sentryClient
		if (typeof window === 'undefined') {
			return false;
		}

		const environment = getEnvironment(env);

		// filter integrations that use the global variable
		const integrations = getDefaultIntegrations({}).filter((defaultIntegration) => {
			return !['BrowserApiErrors', 'Breadcrumbs', 'GlobalHandlers'].includes(
				defaultIntegration.name,
			);
		});

		// NAVX-4946: gate BE-noise suppression behind a feature flag.
		const isSentryFilteringEnabled = fg('navx-4946-sentry-filtering');

		sentryClient = new BrowserClient({
			...DEFAULT_SENTRY_CONFIG,
			ignoreErrors: isSentryFilteringEnabled
				? [...DEFAULT_SENTRY_CONFIG.ignoreErrors, ...SUPPRESSED_BACKEND_ERROR_PATTERNS]
				: DEFAULT_SENTRY_CONFIG.ignoreErrors,
			dsn,
			environment,
			beforeSend: (event) => {
				// Only events captured via logException (which sets teamName)
				if (event.tags?.teamName === undefined) {
					return null;
				}
				if (isSentryFilteringEnabled && isSuppressedBackendError(event)) {
					return null;
				}
				return event;
			},
			attachStacktrace: true,
			transport: makeFetchTransport,
			stackParser: defaultStackParser,
			integrations,
		});

		scope.setClient(sentryClient);
		sentryClient.init();
	}
	return true;
}

/** @internal Reset the Sentry client singleton. Test-only. */
export function __resetSentryClient(): void {
	sentryClient = undefined;
	scope = new Scope();
}
